/* eslint-disable no-param-reassign */

import { v4 } from "uuid";
import { toBase64 } from "@/utils/toBase64";
import {
  INITIAL_INSCRIPTION_PRICE,
  INITIAL_KEEP_BACKGROUND_PRICE,
  INITIAL_DIGITAL_PREVIEW_PRICE,
  INITIAL_PERSONAL_NOTE_PRICE,
  INITIAL_RECTANGLE_KEYCHAIN_PRICE,
  INITIAL_HEART_KEYCHAIN_PRICE,
  LIGHTBASE_PRICES,
  INITIAL_METALLIC_PLATE_PRICE,
  INITIAL_POPUP_CARD_PRICE,
} from "@/consts/orderInitialValues";
import { ORDER_TYPES } from "@/consts/newOrderFormInputs";
import { useOptionsStoreActions } from "@/hooks/store/useOptionsStore";
import { removeEmpty, removeEmptyString } from "./removeEmptyProperty";
import { findOption } from "./optionValue";

// Read the store when an order is built, never at import: this module is reached from
// the modal registry, which the app loads before it installs Pinia.
const productsOptionsNow = () => useOptionsStoreActions().productsOptions;

/* eslint-disable camelcase */
const makeDiscount = (is, type, value) => {
  if (is) {
    return {
      discount_type: type,
      discount: value,
    };
  }
  return {};
};

export const collectOrderRequestBody = async (data, products, rawType) => {
  const {
    type,
    seller_id,
    store_id,
    user_id,
    team_id,
    verify_by,
    transactionId,
    currency,
    name,
    email,
    phone,
    shipping_type,
    service_id,
    country_id,
    state_prov_region,
    city,
    address_line_1,
    address_line_2,
    postcode,
    shipping_cost,
    tax,
  } = data;

  const newProduct = {
    shipping_cost,
    tax,
    transactionId,
    type,
    ...(user_id !== 0 && { user_id }),
    team_id,
    verify_by,
    ...((type === ORDER_TYPES[0].value.toString() || type === ORDER_TYPES[3].value.toString()) && {
      seller_id,
      store_id,
    }),
    currency,
    service_id,
    shipping_address: {
      email,
      phone,
      name,
      city,
      state: state_prov_region,
      zip: postcode,
      country: country_id,
      street: [address_line_1, address_line_2],
    },
    products: [],
  };

  if (shipping_type) {
    newProduct.shipping_type = shipping_type;
  }

  const newProducts = products.map(async (product) => {
    const productHasNoImage = findOption(productsOptionsNow().value, product?.product)?.image;
    let productData = removeEmpty({
      product_id: product.product,
      quantity: product.qty,
      boxes: product.boxQty,
      cost: product.price,
      client_id: product.clientId,
      ...makeDiscount(
        product.productDiscount,
        product.productDiscountType,
        product.productDiscountPrice
      ),
      flag: product.flag,
    });
    if (product.rectangleQty) {
      productData = {
        ...productData,
        rectangle_keychains: removeEmpty({
          qty: product.rectangleQty,
          price: product.rectanglePrice,
          ...makeDiscount(
            product.rectangleDiscount,
            product.rectangleDiscountType,
            product.rectangleDiscountPrice
          ),
        }),
      };
    }
    if (product.heartQty) {
      productData = {
        ...productData,
        heart_keychains: removeEmpty({
          qty: product.heartQty,
          price: product.heartPrice,
          ...makeDiscount(
            product.heartDiscount,
            product.heartDiscountType,
            product.heartDiscountPrice
          ),
        }),
      };
    }
    if (product.lightBaseType) {
      productData = {
        ...productData,
        lightbase: removeEmptyString({
          value: product.lightBaseType,
          qty: product.lightBaseQty,
          price: product.lightBasePrice,
          ...makeDiscount(
            product.lightBaseDiscount,
            product.lightBaseDiscountType,
            product.lightBaseDiscountPrice
          ),
        }),
      };
    }
    if (product.inscriptionFirst || product.inscriptionSecond) {
      productData = {
        ...productData,
        inscription: removeEmpty({
          value: [product.inscriptionFirst, product.inscriptionSecond],
          price: product.inscriptionPrice,
          ...makeDiscount(
            product.inscriptionDiscount,
            product.inscriptionDiscountType,
            product.inscriptionDiscountPrice
          ),
        }),
      };
    }
    if (product.popupCardType) {
      productData["3d_pop_up_card"] = {
        value: product.popupCardType,
        qty: product.popupCardQty,
        price: product.popupCardPrice,
        ...makeDiscount(
          product.popupCardDiscount,
          product.popupCardDiscountType,
          product.popupCardDiscountPrice
        ),
      };
    }
    if (product.background) {
      productData.keep_the_background = {
        price: product.backgroundPrice,
        ...makeDiscount(
          product.backgroundDiscount,
          product.backgroundDiscountType,
          product.backgroundDiscountPrice
        ),
      };
    }
    if (product.notes) {
      productData.personal_gift_note = {
        value: product.notesText,
        price: product.notesPrice,
        ...makeDiscount(
          product.notesDiscount,
          product.notesDiscountType,
          product.notesDiscountPrice
        ),
      };
    }
    if (product.metallicPlateText) {
      productData.metal_plate_for_base = {
        value: product.metallicPlateText,
        price: product.metallicPlatePrice,
        ...makeDiscount(
          product.metallicPlateDiscount,
          product.metallicPlateDiscountType,
          product.metallicPlateDiscountPrice
        ),
      };
    }
    if (product.digitalPreview) {
      productData.digital_preview = product.digitalPreview && {
        price: product.digitalPreviewPrice,
        ...makeDiscount(
          product.digitalPreviewDiscount,
          product.digitalPreviewDiscountType,
          product.digitalPreviewDiscountPrice
        ),
      };
    }
    if (!product.photoLater && productHasNoImage && !rawType) {
      productData = {
        ...productData,
        photo: await toBase64(product.mainPhoto[0].file),
        photo_extra: await toBase64(product.extraPhoto[0].file),
        without_photo:
          product.photoLater === null ? (product.photoLater = false) : product.photoLater,
      };
    }
    if (product.photoLater) {
      productData = {
        ...productData,
        without_photo: product.photoLater,
      };
    }
    return productData;
  });

  const newProductsData = Promise.all(newProducts);
  newProduct.products = await newProductsData;

  return newProduct;
};

export const collectEditOrderRequestBody = async (data, products) => {
  const {
    currency,
    seller_id,
    store_id,
    name,
    email,
    phone,
    shipping_type,
    service_id,
    transactionId,
    country_id,
    state,
    city,
    address_line_1,
    address_line_2,
    zip,
    shipping_cost,
    tax,
    skip_receipt,
  } = data;

  const editedProduct = {
    seller_id,
    store_id,
    shipping_cost,
    tax,
    transactionId,
    currency,
    service_id,
    skip_receipt,
    shipping_address: {
      email,
      phone,
      name,
      city,
      state,
      zip,
      country: country_id,
      street: [address_line_1, address_line_2],
    },
    products: [],
  };

  if (shipping_type) {
    editedProduct.shipping_type = shipping_type;
  }

  const newProducts = products.map(async (product) => {
    const productHasNoImage = findOption(productsOptionsNow().value, product?.product)?.image;
    let productData = removeEmpty({
      product_id: product.product,
      quantity: product.qty,
      boxes: product.boxQty,
      cost: product.price,
      client_id: product.clientId,
      shipping_type: product.shipping_type,
      ...makeDiscount(
        product.productDiscount,
        product.productDiscountType,
        product.productDiscountPrice
      ),
      flag: product.flag,
    });
    if (product.id) {
      productData.id = product.id;
    }
    if (product.rectangleQty) {
      productData = {
        ...productData,
        rectangle_keychains: removeEmpty({
          qty: product.rectangleQty,
          price: product.rectanglePrice,
          ...makeDiscount(
            product.rectangleDiscount,
            product.rectangleDiscountType,
            product.rectangleDiscountPrice
          ),
        }),
      };
    }
    if (product.heartQty) {
      productData = {
        ...productData,
        heart_keychains: removeEmpty({
          qty: product.heartQty,
          price: product.heartPrice,
          ...makeDiscount(
            product.heartDiscount,
            product.heartDiscountType,
            product.heartDiscountPrice
          ),
        }),
      };
    }
    if (product.lightBaseType) {
      productData = {
        ...productData,
        lightbase: removeEmptyString({
          value: product.lightBaseType,
          qty: product.lightBaseQty,
          price: product.lightBasePrice,
          ...makeDiscount(
            product.lightBaseDiscount,
            product.lightBaseDiscountType,
            product.lightBaseDiscountPrice
          ),
        }),
      };
    }
    if (product.inscriptionFirst || product.inscriptionSecond) {
      productData = {
        ...productData,
        inscription: removeEmpty({
          value: [product.inscriptionFirst, product.inscriptionSecond],
          price: product.inscriptionPrice,
          ...makeDiscount(
            product.inscriptionDiscount,
            product.inscriptionDiscountType,
            product.inscriptionDiscountPrice
          ),
        }),
      };
    }
    if (product.background) {
      productData.keep_the_background = {
        price: product.backgroundPrice,
        ...makeDiscount(
          product.backgroundDiscount,
          product.backgroundDiscountType,
          product.backgroundDiscountPrice
        ),
      };
    }
    if (product.notesText) {
      productData.personal_gift_note = {
        value: product.notesText,
        price: product.notesPrice,
        ...makeDiscount(
          product.notesDiscount,
          product.notesDiscountType,
          product.notesDiscountPrice
        ),
      };
    }
    if (product.metallicPlateText) {
      productData.metal_plate_for_base = {
        value: product.metallicPlateText,
        price: product.metallicPlatePrice,
        ...makeDiscount(
          product.metallicPlateDiscount,
          product.metallicPlateDiscountType,
          product.metallicPlateDiscountPrice
        ),
      };
    }
    if (product.popupCardType) {
      productData["3d_pop_up_card"] = {
        value: product.popupCardType,
        qty: product.popupCardQty,
        price: product.popupCardPrice,
        ...makeDiscount(
          product.popupCardDiscount,
          product.popupCardDiscountType,
          product.popupCardDiscountPrice
        ),
      };
    }
    if (product.digitalPreview) {
      productData.digital_preview = product.digitalPreview && {
        price: product.digitalPreviewPrice,
        ...makeDiscount(
          product.digitalPreviewDiscount,
          product.digitalPreviewDiscountType,
          product.digitalPreviewDiscountPrice
        ),
      };
    }
    if (productHasNoImage) {
      if (product.mainPhoto?.[0]?.file) {
        productData = {
          ...productData,
          photo: await toBase64(product.mainPhoto[0].file),
        };
      }
    }
    return productData;
  });

  const newProductsData = Promise.all(newProducts);
  editedProduct.products = await newProductsData;

  return editedProduct;
};

export const collectInitialFormData = (data) => {
  return {
    seller_id: data.seller_id,
    store_id: data.store_id,
    currency: data.currency,
    transactionId: data.transactionId,
    name: data.shipping_address.name,
    email: data.shipping_address.email,
    phone: data.shipping_address.phone,
    shipping_type: data.shipping_type,
    service_id: data.service_id,
    country_id: data.shipping_address.country,
    state: data.shipping_address.state,
    city: data.shipping_address.city,
    address_line_1: data.shipping_address.street[0],
    address_line_2: data.shipping_address.street[1],
    zip: data.shipping_address.zip,
  };
};

export const collectInitialAdditionalFormData = (data) => {
  return {
    shipping_cost: data.shipping_cost?.toString(),
    tax: data.tax?.toString() || "0",
    skip_receipt: false,
  };
};

export const collectInitialProductsData = (data) => {
  return data.products.map((product) => ({
    id: product.id,
    card_id: v4(),
    name: "Product",
    product: product.product_id,
    qty: product.quantity,
    boxQty: product.boxes,
    price: product.cost,
    productDiscount: !!product.discount,
    productDiscountType: product.discount_type,
    productDiscountPrice: product.discount,
    clientId: product.client_id,
    seller_id: product.seller_id,
    store_id: product.store_id,

    mainPhoto: null,

    flag: product.flag,

    rectangleQty: product.rectangle_keychains?.qty || null,
    rectanglePrice: product.rectangle_keychains?.price || INITIAL_RECTANGLE_KEYCHAIN_PRICE,
    rectangleDiscount: !!product.rectangle_keychains?.discount || null,
    rectangleDiscountType: product.rectangle_keychains?.discount_type || null,
    rectangleDiscountPrice: product.rectangle_keychains?.discount || null,

    heartQty: product.heart_keychains?.qty || null,
    heartPrice: product.heart_keychains?.price || INITIAL_HEART_KEYCHAIN_PRICE,
    heartDiscount: !!product.heart_keychains?.discount || null,
    heartDiscountType: product.heart_keychains?.discount_type || null,
    heartDiscountPrice: product.heart_keychains?.discount || null,

    lightBaseType: product.lightbase?.value || "",
    lightBaseQty: product.lightbase?.qty || null,
    lightBasePrice: product.lightbase?.price || LIGHTBASE_PRICES[product.lightbase?.value],
    lightBaseDiscount: !!product.lightbase?.discount || null,
    lightBaseDiscountType: product.lightbase?.discount_type || null,
    lightBaseDiscountPrice: product.lightbase?.discount || null,

    inscriptionFirst: product.inscription?.value[0] || null,
    inscriptionSecond: product.inscription?.value[1] || null,
    inscriptionPrice: product.inscription?.price || INITIAL_INSCRIPTION_PRICE,
    inscriptionDiscount: !!product.inscription?.discount || null,
    inscriptionDiscountType: product.inscription?.discount_type || null,
    inscriptionDiscountPrice: product.inscription?.discount || null,

    background: !!product.keep_the_background || null,
    backgroundPrice: product.keep_the_background?.price || INITIAL_KEEP_BACKGROUND_PRICE,
    backgroundDiscount: !!product.keep_the_background?.discount || null,
    backgroundDiscountType: product.keep_the_background?.discount_type || null,
    backgroundDiscountPrice: product.keep_the_background?.discount || null,

    digitalPreview: !!product.digital_preview || null,
    digitalPreviewPrice: product.digital_preview?.price || INITIAL_DIGITAL_PREVIEW_PRICE,
    digitalPreviewDiscount: !!product.digital_preview?.discount || null,
    digitalPreviewDiscountType: product.digital_preview?.discount_type || null,
    digitalPreviewDiscountPrice: product.digital_preview?.discount || null,

    notes: !!product.personal_gift_note || null,
    notesText: product.personal_gift_note?.value || null,
    notesPrice: product.personal_gift_note?.price || INITIAL_PERSONAL_NOTE_PRICE,
    notesDiscount: !!product.personal_gift_note?.discount || null,
    notesDiscountType: product.personal_gift_note?.discount_type || null,
    notesDiscountPrice: product.personal_gift_note?.discount || null,

    metallicPlate: !!product.metal_plate_for_base || null,
    metallicPlateText: product.metal_plate_for_base?.value || null,
    metallicPlatePrice: product.metal_plate_for_base?.price || INITIAL_METALLIC_PLATE_PRICE,
    metallicPlateDiscount: !!product.metal_plate_for_base?.discount || null,
    metallicPlateDiscountType: product.metal_plate_for_base?.discount_type || null,
    metallicPlateDiscountPrice: product.metal_plate_for_base?.discount || null,

    popupCardType: product["3d_pop_up_card"]?.value || "",
    popupCardDiscount: !!product["3d_pop_up_card"]?.discount || null,
    popupCardPrice: product["3d_pop_up_card"]?.price || INITIAL_POPUP_CARD_PRICE,
    popupCardDiscountType: product["3d_pop_up_card"]?.discount_type || null,
    popupCardDiscountPrice: product["3d_pop_up_card"]?.discount || null,
    popupCardQty: product["3d_pop_up_card"]?.qty || null,
  }));
};

export const checkRaw = (type) => {
  return type === ORDER_TYPES[2].value || type === ORDER_TYPES[3].value;
};
